import {FormEvent, useEffect, useState} from "react";
import {Alert, Card, Col, Form, Row} from "react-bootstrap";
import {ProfileApi} from "../../api/ProfileApi";
import {UserViewModel} from "../../api/models";
import {useAuth} from "../../auth/AuthContext";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {SubmitButton} from "../../components/SubmitButton";
import {emailProblem, MIN_PASSWORD_LENGTH, newPasswordProblem} from "../../components/format";

export function ProfilePage() {
    const {data: profile, error, loading, setData} = useAsync(() => ProfileApi.get(), []);
    return (
        <>
            <PageHeader title="Profile"/>
            {loading && <LoadingSpinner/>}
            <ErrorAlert error={error}/>
            {profile && (
                <Row className="g-4">
                    <Col lg={6}><DetailsCard profile={profile} onSaved={setData}/></Col>
                    <Col lg={6}><EmailCard profile={profile}/></Col>
                    <Col lg={6}><PasswordCard/></Col>
                </Row>
            )}
        </>
    );
}

function DetailsCard({profile, onSaved}: { profile: UserViewModel, onSaved(profile: UserViewModel): void }) {
    const {renew} = useAuth();
    const [displayName, setDisplayName] = useState(profile.displayName);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<unknown>();

    useEffect(() => setDisplayName(profile.displayName), [profile.displayName]);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            // The API replaces every profile field: send the current values of the ones this form doesn't edit.
            const updated = await ProfileApi.update({
                displayName: displayName.trim(),
                avatarUrl: profile.avatarUrl,
                timezone: profile.timezone,
                language: profile.language,
                defaultProjectId: profile.defaultProjectId
            });
            onSaved(updated);
            setSaved(true);
            // The navbar shows the name from the access token.
            await renew().catch(() => undefined);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Title className="mb-3">Details</Card.Title>
                <ErrorAlert error={error}/>
                {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                <Form onSubmit={submit}>
                    <Form.Group className="mb-3" controlId="profile-username">
                        <Form.Label>Username</Form.Label>
                        <Form.Control value={profile.username} readOnly plaintext/>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="profile-display-name">
                        <Form.Label>Display name</Form.Label>
                        <Form.Control required maxLength={100} value={displayName} onChange={e => setDisplayName(e.target.value)}/>
                    </Form.Group>
                    <SubmitButton busy={busy}>Save</SubmitButton>
                </Form>
            </Card.Body>
        </Card>
    );
}

function EmailCard({profile}: { profile: UserViewModel }) {
    const [email, setEmail] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [sentTo, setSentTo] = useState<string>();
    const [error, setError] = useState<unknown>();
    const problem = emailProblem(email);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (problem) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await ProfileApi.changeEmail(email.trim());
            setSentTo(email.trim());
            setEmail('');
            setValidated(false);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Title className="mb-3">Email address</Card.Title>
                <p>Current address: <strong>{profile.emailAddress}</strong></p>
                <ErrorAlert error={error}/>
                {sentTo && (
                    <Alert variant="info" dismissible onClose={() => setSentTo(undefined)}>
                        We've sent a confirmation link to <strong>{sentTo}</strong>. Open it within 10 minutes to finish
                        the change. Until then, your address stays the same.
                    </Alert>
                )}
                <Form noValidate onSubmit={submit}>
                    <Form.Group className="mb-3" controlId="profile-new-email">
                        <Form.Label>New email address</Form.Label>
                        <Form.Control type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} isInvalid={validated && !!problem}/>
                        <Form.Control.Feedback type="invalid">{problem}</Form.Control.Feedback>
                    </Form.Group>
                    <SubmitButton busy={busy}>Send confirmation link</SubmitButton>
                </Form>
            </Card.Body>
        </Card>
    );
}

function PasswordCard() {
    const [current, setCurrent] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<unknown>();
    const problem = newPasswordProblem(password, confirm);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (problem || !current) {
            return;
        }
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            await ProfileApi.changePassword(current, password);
            setSaved(true);
            setCurrent('');
            setPassword('');
            setConfirm('');
            setValidated(false);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="h-100">
            <Card.Body>
                <Card.Title className="mb-3">Password</Card.Title>
                <ErrorAlert error={error} messages={{ 400: 'The current password is incorrect.' }}/>
                {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Your password has been changed.</Alert>}
                <Form noValidate onSubmit={submit}>
                    <Form.Group className="mb-3" controlId="profile-current-password">
                        <Form.Label>Current password</Form.Label>
                        <Form.Control type="password" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)}
                                      isInvalid={validated && !current}/>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="profile-new-password">
                        <Form.Label>New password</Form.Label>
                        <Form.Control type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)}
                                      isInvalid={validated && password.length < MIN_PASSWORD_LENGTH}/>
                        <Form.Text>At least {MIN_PASSWORD_LENGTH} characters.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="profile-confirm-password">
                        <Form.Label>Confirm new password</Form.Label>
                        <Form.Control type="password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)}
                                      isInvalid={validated && !!problem}/>
                        <Form.Control.Feedback type="invalid">{problem}</Form.Control.Feedback>
                    </Form.Group>
                    <SubmitButton busy={busy}>Change password</SubmitButton>
                </Form>
            </Card.Body>
        </Card>
    );
}
